import { EMPTY_LIST, EMPTY_STRING } from '../../../../constants/common'
import {
  EMAIL_REPLY_FORM_HTML_BLOCK_ID,
  EMAIL_REPLY_FORM_HTML_QUOTED_HISTORY_CLASSNAME,
} from '../../../../constants/email'
import { toUkTimezoneDate } from '../../../../utils/date'
import { getDatePickerMonthOptions } from '../../../DateTime'
import {
  toEmailRecipientSelectItem,
  toEmailRecipientSelectItems,
} from '../../Email.utils'
import {
  EMAIL_CREATION_FORM_FIELD_NAME_CC,
  EMAIL_CREATION_FORM_FIELD_NAME_FILES,
  EMAIL_CREATION_FORM_FIELD_NAME_MESSAGE,
  EMAIL_CREATION_FORM_FIELD_NAME_REPLY_SUBJECT,
  EMAIL_CREATION_FORM_FIELD_NAME_REPLY_TEXT,
  EMAIL_CREATION_FORM_FIELD_NAME_SUBJECT,
  EMAIL_CREATION_FORM_FIELD_NAME_THREAD,
  EMAIL_CREATION_FORM_FIELD_NAME_TO,
} from '../../EmailCreationForm'

import { EMAIL_REPLY_FORM_MODE } from './useEmailReply.constants'

// `forwarded` marks an attachment inherited from the forwarded message: the file
// still belongs to that message, so the form must not delete it on the backend.
function toEmailFormFile({ filename, ...restFile }) {
  return {
    ...restFile,
    forwarded: true,
    name: filename,
  }
}

// Senders without a name (e.g. Sentry) render an empty avatar circle; fall back
// to the email for the avatar text. (Not `fullname` — it drives the chip label.)
function toReplyRecipientSelectItem(item) {
  const selectItem = toEmailRecipientSelectItem(item)

  return {
    ...selectItem,
    avatar: {
      ...selectItem.avatar,
      text: selectItem.avatar.text || item.email,
    },
  }
}

function getReplyMessageReplyText({ data, t, timeZone }) {
  const { createdDate, createdDateTime } = getReplyMessageCreatedDateTime({
    created: data.created,
    t,
    timeZone,
  })

  return [
    '<br>',
    '<br>',
    `<div id="${EMAIL_REPLY_FORM_HTML_BLOCK_ID}" class="${EMAIL_REPLY_FORM_HTML_QUOTED_HISTORY_CLASSNAME}">`,
    `<p>${t('EmailProfile.ReplyFormQuoteHeaderKey', {
      userEmail: data.sender.email,
      userName: data.sender.name || data.owner.fullname,
      wroteAtTime: createdDateTime,
      wroteOnDate: createdDate,
    })
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')}</p>`,
    `<blockquote>${data.text}</blockquote>`,
    `</div>`,
  ].join(EMPTY_STRING)
}

function getForwardMessageReplyText({ data, t, timeZone }) {
  function joinUsers(users) {
    return users
      .map(user => `${user.fullname} &lt;${user.email}&gt;`)
      .join(', ')
  }

  return [
    '<br>',
    '<br>',
    `<div id="${EMAIL_REPLY_FORM_HTML_BLOCK_ID}">`,
    `<p>---------- ${t('EmailProfile.ForwardFormTitleKey')} ----------</p>`,
    `<p>${t('EmailProfile.ForwardFormSenderLabelKey')}: <b>${data.sender.name || data.owner.fullname || EMPTY_STRING}</b>${data.sender.email ? ` &lt;${data.sender.email}&gt;` : EMPTY_STRING}</p>`,
    `<p>${t('EmailProfile.ForwardFormSentLabelKey')}: ${getForwardMessageCreatedString({ created: data.created, t, timeZone })}</p>`,
    `<p>${t('EmailProfile.ForwardFormSubjectLabelKey')}: ${data.subject}</p>`,
    ...(data.to.length > 0
      ? [
          `<p>${t('EmailProfile.ForwardFormRecipientsLabelKey')}: ${joinUsers(data.to)}</p>`,
        ]
      : EMPTY_LIST),
    ...(data.cc.length > 0
      ? [
          `<p>${t('EmailProfile.ForwardFormCcLabelKey')}: ${joinUsers(data.cc)}</p>`,
        ]
      : EMPTY_LIST),
    '<p>&nbsp;</p>',
    data.text,
    `</div>`,
  ].join(EMPTY_STRING)
}

function getCommonReplyMessageInitialValues(data, replyText) {
  return {
    [EMAIL_CREATION_FORM_FIELD_NAME_REPLY_SUBJECT]: data.subject,
    [EMAIL_CREATION_FORM_FIELD_NAME_REPLY_TEXT]: replyText,
    [EMAIL_CREATION_FORM_FIELD_NAME_SUBJECT]: data.subject,
    [EMAIL_CREATION_FORM_FIELD_NAME_THREAD]: data.email_thread_uuid,
  }
}

function getReplyMessageInitialValues({ data, t, timeZone }) {
  const replyText = getReplyMessageReplyText({
    data,
    t,
    timeZone,
  })

  return {
    ...getCommonReplyMessageInitialValues(data, replyText),
    [EMAIL_CREATION_FORM_FIELD_NAME_MESSAGE]: replyText,
    [EMAIL_CREATION_FORM_FIELD_NAME_TO]: toEmailRecipientSelectItems(
      data.outgoing ? data.to : [data.owner],
      toReplyRecipientSelectItem
    ),
  }
}

function getReplyAllMessageInitialValues({ currentUserId, data, t, timeZone }) {
  const filteredTo = data.to.filter(item => item.id !== currentUserId)

  const replyText = getReplyMessageReplyText({
    data,
    t,
    timeZone,
  })

  return {
    ...getCommonReplyMessageInitialValues(data, replyText),
    [EMAIL_CREATION_FORM_FIELD_NAME_CC]: toEmailRecipientSelectItems(
      data.cc,
      toReplyRecipientSelectItem
    ),
    [EMAIL_CREATION_FORM_FIELD_NAME_MESSAGE]: replyText,
    [EMAIL_CREATION_FORM_FIELD_NAME_TO]: toEmailRecipientSelectItems(
      data.outgoing ? filteredTo : [data.owner, ...filteredTo],
      toReplyRecipientSelectItem
    ),
  }
}

function getForwardMessageInitialValues({ data, t, timeZone }) {
  const replyText = getForwardMessageReplyText({
    data,
    t,
    timeZone,
  })

  return {
    ...getCommonReplyMessageInitialValues(data, replyText),
    [EMAIL_CREATION_FORM_FIELD_NAME_FILES]: data.files.map(toEmailFormFile),
    [EMAIL_CREATION_FORM_FIELD_NAME_MESSAGE]: replyText,
  }
}

function getReplyMessageCreatedDateTime({ created, t, timeZone }) {
  const ukTimeZoneCreated = toUkTimezoneDate(created, timeZone)

  const day = String(ukTimeZoneCreated.getDate()).padStart(2, '0')
  const month = getDatePickerMonthOptions(t)[ukTimeZoneCreated.getMonth()]
  const year = ukTimeZoneCreated.getFullYear()

  const hours = String(ukTimeZoneCreated.getHours()).padStart(2, '0')
  const minutes = String(ukTimeZoneCreated.getMinutes()).padStart(2, '0')

  const createdDate = `${day} ${month.label} ${year}`
  const createdDateTime = `${hours}:${minutes}`

  return { createdDate, createdDateTime }
}

function getForwardMessageCreatedString({ created, t, timeZone }) {
  const date = toUkTimezoneDate(created, timeZone)

  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  const day = String(date.getDate()).padStart(2, '0')
  const month = getDatePickerMonthOptions(t)[date.getMonth()].label
  const year = date.getFullYear()

  return `${hours}:${minutes}, ${day} ${month} ${year}`
}

export function getReplyInitialValuesByMode(mode) {
  switch (mode) {
    case EMAIL_REPLY_FORM_MODE.replyAll:
      return getReplyAllMessageInitialValues

    case EMAIL_REPLY_FORM_MODE.forward:
      return getForwardMessageInitialValues

    default:
      return getReplyMessageInitialValues
  }
}

// Drafts saved before the reply subject field existed are missing it: restore it from the replied message
export function getEmailReplyDraftInitialValues({ data, draft }) {
  return {
    ...draft,
    [EMAIL_CREATION_FORM_FIELD_NAME_REPLY_SUBJECT]: data.subject,
  }
}
